import {
	useEffect,
	useRef,
	InputHTMLAttributes,
	ReactNode,
	useState,
} from 'react';
import getUnicodeFlagIcon from 'country-flag-icons/unicode';
import { selectArrow } from 'assets';

export interface TextInputProps
	extends InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> {
	passwordInput?: boolean;
	prefixLabel?: any;
	suffixLabel?: ReactNode;
	setValue?: (e?: any) => void;
	required?: boolean;
	isClickable?: boolean;
	label: string;
	labelIcon?: string;
	subLabel?: ReactNode;
	errorMessage?: any;
	inputInfo?: ReactNode;
	icon?: ReactNode;
	iconClick?: () => void;
	prefix?: string;
	onClick?: (e: any) => void;
	onChange?: (e: any) => void;
	options: any;
	selectOption: (e: any) => void;
}

const SearchableCountries = ({
	options,
	label,
	name,
	placeholder,
	isClickable,
	selectOption,
	...rest
}: TextInputProps) => {
	const [query, setQuery] = useState('');
	const [isOpen, setIsOpen] = useState(false);
	const [selectedVal, setSelectedVal] = useState('');
	const [optionsArray, setOptionsArray] = useState<any[]>([]);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const searchInputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		setOptionsArray(options);
	}, [options]);

	// Close dropdown on outside click
	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (
				wrapperRef.current &&
				!wrapperRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
			}
		}
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	// Auto-focus search input when dropdown opens
	useEffect(() => {
		if (isOpen && searchInputRef.current) {
			searchInputRef.current.focus();
		}
	}, [isOpen]);

	const handleChange = (e: any) => {
		const { value } = e.target;
		setQuery(value);

		if (value?.length > 0) {
			handleSearch(value);
		} else {
			setOptionsArray(options);
		}
	};

	const handleSearch = (value: string) => {
		const newList = options.filter((item: any) =>
			item?.name.toLowerCase().includes(value.toLowerCase())
		);
		setOptionsArray(newList);
	};

	useEffect(() => {
		setQuery(selectedVal);
	}, [selectedVal]);

	const defaultOption: any = optionsArray?.find(
		(option: any) => option?.code === name
	);

	return (
		<div ref={wrapperRef}>
			<div className="w-full flex items-center mb-1">
				<label className="text-[13px] md:text-[14px] capitalize leading-[21px] font-[400] text-grey font-medium">
					{label}
				</label>
			</div>
			<div className="relative">
				<div className="relative">
					<input
						{...rest}
						readOnly
						value={selectedVal || defaultOption?.name || ''}
						placeholder={placeholder}
						autoComplete="off"
						onClick={
							isClickable === false ? undefined : () => setIsOpen(!isOpen)
						}
						className={`w-full h-[56px] rounded-[43px] bg-[#0F121D] outline-0 px-3 text-[0.875rem] text-center font-medium placeholder-[#232838] border-2 border-[#232838] leading-normal ${
							query ? 'text-white border-[#84C70D]' : 'text-grey'
						}`}
					/>
					<div
						className={`absolute top-[32%] right-[12px] transition-transform duration-200 ${
							isOpen ? 'rotate-180' : 'rotate-0'
						}`}
						onClick={() => setIsOpen(!isOpen)}
						data-testid="select-country-arrow"
					>
						{isClickable !== false && <img src={selectArrow} alt="arrow" />}
					</div>
				</div>

				{isOpen && (
					<div className="absolute z-10 w-full py-4 bg-[#0F121D] text-white rounded-lg overflow-auto max-h-[400px]">
						<div className="mb-4">
							<input
								{...rest}
								type="text"
								ref={searchInputRef}
								value={query || defaultOption?.name || ''}
								placeholder={placeholder}
								name={name}
								autoComplete="off"
								onChange={handleChange}
								data-testid="search-country-input"
								className={`w-full h-[56px] rounded-[43px] bg-[#0F121D] outline-0 px-3 text-[0.875rem] text-center font-medium placeholder-[#232838] border-2 border-[#232838] leading-normal ${
									query ? 'text-white border-[#84C70D]' : 'text-grey'
								}`}
							/>
						</div>

						{optionsArray?.length > 0 &&
							optionsArray.map((option: any, index: number) => (
								<div
									onClick={() => {
										setSelectedVal(option.name);
										selectOption(option);
										setIsOpen(false);
									}}
									key={`${option.name}-${index}`}
									className="flex items-center gap-4 mb-2 px-6 cursor-pointer hover:bg-[#1a1e2e]"
									data-testid="select-a-country"
								>
									<span>{getUnicodeFlagIcon(option?.code)}</span>
									<span>{option.name}</span>
								</div>
							))}
					</div>
				)}
			</div>
		</div>
	);
};

export default SearchableCountries;
